import { Box, Button, Snackbar, Alert, Radio, Tooltip, IconButton } from '@mui/material';
import { NoContent, ReactTable } from '../../../components/shared';
import { useState, useEffect } from 'react';
import { useGetModemHistoryByIdQuery, useDeleteModemHistoryMutation } from '../../../services/Modems/ModemsService';
import { getDateFormat, getTimeFormat } from '../../../utils/common-methods';
import useStyles from '../Modems/style';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { COLUMNS } from '../../../constants/tableColumns';
import { routeConstants } from '../../../constants/routeConstants';
import { useNavigate } from 'react-router-dom';
import { Delete } from '@mui/icons-material';
import ConfirmationModal from '../../../components/ConfirmationModel';

const DEFAULT_PAGE = 1;
const DEFAULT_PER_PAGE = 30;

export default function ModemHistoryModel({ modemId }) {
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
  const [perPageNumber, setPerPageNumber] = useState(DEFAULT_PER_PAGE);
  const [tableData, setTableData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedRow, setSelectedRow] = useState(null);
  const classes = useStyles();
  const navigate = useNavigate();
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);

  const [deleteModemHistory, { isLoading: isDeleting }] = useDeleteModemHistoryMutation();

  // API query with additional options for cache management
  const {
    data: latestData,
    isError,
    isSuccess,
    isFetching,
    isLoading,
    refetch,
  } = useGetModemHistoryByIdQuery(
    {
      modemId,
      page: currentPage,
    },
    {
      skip: !modemId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true, // Refetch when component gains focus
    },
  );

  const hasTableData = !isLoading && !isFetching && !isError && tableData && tableData?.length > 0;
  const noTableData = !isLoading && !isFetching && !isError && tableData && tableData?.length === 0;

  // Force refetch when modal opens with new modemId
  useEffect(() => {
    if (modemId) {
      refetch();
    }
  }, [modemId, refetch]);

  const formatChanges = (changes) => {
    if (!changes) return '';
    // List of fields to skip
    const skipFields = ['inEffectFrom', 'inEffectTo'];
    if (Array.isArray(changes) && changes?.length > 0 && typeof changes[0] === 'object') {
      return changes
        .filter((change) => !skipFields.includes(change.field))
        .map((change) => `${change.field} : ${change.from ?? 'N/A'} --> ${change.to ?? 'N/A'}`)
        .join('\n');
    }
    if (Array.isArray(changes)) {
      return changes.join('\n');
    }
    return '';
  };

  // Update tableData when API data is received
  useEffect(() => {
    if (isSuccess && latestData?.modemHistory) {
      const mappedData = latestData.modemHistory.map((history, idx) => ({
        ...history,
        serialNumber: history?.modem?.serialNumber || 'N/A',
        type: history?.modem?.type || 'N/A',
        assetName: history?.asset?.title || 'N/A',
        inEffectFrom: history?.inEffectFrom ? getDateFormat(history.inEffectFrom) : 'N/A',
        inEffectTo: history?.inEffectTo ? getDateFormat(history.inEffectTo) : '',
        updatedBy:
          history?.updatedByUser?.firstName && history?.updatedByUser?.lastName
            ? `${history.updatedByUser.firstName} ${history.updatedByUser.lastName}`
            : 'N/A',
        updatedAt: history?.updatedAt
          ? `${getDateFormat(history.updatedAt)} ${getTimeFormat(history.updatedAt)}`
          : 'N/A',
        originalInEffectTo: history?.inEffectTo,
        modemId: history.modemId,
        version: history.version,
        changes: formatChanges(history.changes) || 'N/A',
        rowKey: idx, // unique key for radio
      }));

      setTableData(mappedData);
      setTotalCount(latestData?.totalCount || 0);

      // Reset selection when data changes
      setSelectedRow(null);

      // If no row selected and data exists, select the first by default
      if (mappedData?.length > 0) {
        setSelectedRow(mappedData[0].rowKey);
      }
    } else if (isError) {
      setTableData([]);
      setTotalCount(0);
      setSelectedRow(null);
      setCurrentPage(1);
    }
  }, [isSuccess, isError, latestData?.modemHistory, latestData?.totalCount]);

  // Separate effect for pagination reset
  useEffect(() => {
    if (latestData?.totalCount !== undefined) {
      const newTotalPages = Math.ceil(latestData.totalCount / perPageNumber);
      if (currentPage > newTotalPages && newTotalPages > 0) {
        setCurrentPage(1);
      }
    }
  }, [latestData?.totalCount, perPageNumber, currentPage]);

  const getRowClassName = (row) => (!row.original.originalInEffectTo ? classes.activeRow : '');

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const pageChangeHandler = (newPage) => {
    setCurrentPage(newPage);
  };

  const perPageChangeHandler = (newPerPage) => {
    setPerPageNumber(newPerPage);
  };

  const handleDeleteClick = (row) => {
    setRowToDelete(row);
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    setConfirmOpen(false);
    setRowToDelete(null);
  };

  const handleConfirmDelete = () => {
    if (!rowToDelete) return;

    deleteModemHistory({ id: rowToDelete.modemId, version: rowToDelete.version })
      .unwrap()
      .then(() => {
        showSnackbar('History record deleted successfully.', 'success');
        refetch();
      })
      .catch((err) => {
        console.error('Failed to delete modem history:', err);
        showSnackbar(err.data?.message || 'Failed to delete history record.', 'error');
      })
      .finally(() => {
        handleConfirmClose();
      });
  };

  // Handler for radio selection
  const handleRadioChange = (rowKey) => {
    setSelectedRow(rowKey);
  };

  // Handler for ADD and EDIT buttons
  const handleActionClick = (actionType) => {
    if (selectedRow === null) {
      showSnackbar('Please select a row first.', 'warning');
      return;
    }
    const rowData = tableData.find((row) => row.rowKey === selectedRow);
    if (!rowData) {
      showSnackbar('Invalid selection.', 'error');
      return;
    }
    navigate(`/${routeConstants.MODEMS_ROUTE}/${routeConstants.EDIT_MODEM_ROUTE}/${rowData.modemId}`, {
      state: { version: rowData.version, type: actionType },
    });
  };

  // Add radio button column as the first column
  const RADIO_COLUMN = {
    Header: '',
    accessor: 'radio',
    disableSortBy: true,
    Cell: ({ row }) => (
      <Radio
        checked={selectedRow === row.original.rowKey}
        onChange={() => handleRadioChange(row.original.rowKey)}
        value={row.original.rowKey}
        inputProps={{ 'aria-label': 'Select row' }}
      />
    ),
    width: 50,
  };

  const COLUMN_DELETE = {
    Header: '',
    accessor: 'delete',
    width: 50,
    Cell: ({ row }) =>
      (row.original.originalInEffectTo && (
        <Tooltip title="Delete">
          <IconButton size="small" onClick={() => handleDeleteClick(row.original)}>
            <Delete fontSize="small" />
          </IconButton>
        </Tooltip>
      )) ||
      '',
  };

  const MODEM_HISTORY_COLUMNS_WITH_CHANGES = [
    RADIO_COLUMN,
    ...COLUMNS.MODEM_HISTORY_COLUMNS.filter(
      (col) => col.accessor !== 'changes' && col.accessor !== 'updatedBy' && col.accessor !== 'updatedAt',
    ),
    {
      Header: 'Changes',
      accessor: 'changes',
      width: '400',
      Cell: ({ value }) => <span style={{ whiteSpace: 'pre-line' }}>{value}</span>,
    },
    ...COLUMNS.MODEM_HISTORY_COLUMNS.filter((col) => col.accessor === 'updatedBy'),
    ...COLUMNS.MODEM_HISTORY_COLUMNS.filter((col) => col.accessor === 'updatedAt'),
    COLUMN_DELETE,
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Box display="flex" mb={2} justifyContent="space-between" alignItems="center">
        <Box sx={{ ml: 'auto' }}>
          <Button variant="contained" color="primary" sx={{ mr: 2 }} onClick={() => handleActionClick('edit')}>
            EDIT
          </Button>
          <Button variant="contained" color="primary" onClick={() => handleActionClick('add')}>
            ADD
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          overflow: 'auto',
        }}
      >
        {(isLoading || isFetching) && <LoadingSpinner containerHeight={300} />}
        {isError && <NoContent title="No History Found" desc="No modem history available for this modem." />}
        {hasTableData && (
          <ReactTable
            key={`history-table-${modemId}-${tableData.length}`} // Force re-render when data changes
            data={tableData || []} // Ensure data is never undefined
            columns={MODEM_HISTORY_COLUMNS_WITH_CHANGES}
            pagination={totalCount > perPageNumber}
            manualPagination={true}
            totalCount={totalCount}
            pageNumberForTablePagination={Math.max(0, currentPage - 1)} // Ensure non-negative
            onPageChange={pageChangeHandler}
            onPerPageChange={perPageChangeHandler}
            perPage={perPageNumber}
            rowClassName={getRowClassName}
          />
        )}
        {noTableData && <NoContent title="No Results Found" desc="No modem history records found." />}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <ConfirmationModal
        isOpen={confirmOpen}
        title="Confirm Deletion"
        msg={`Are you sure you want to delete the history record from ${rowToDelete?.inEffectFrom} to ${rowToDelete?.inEffectTo} ? This action cannot be undone.`}
        buttons={[
          { text: isDeleting ? 'Deleting...' : 'Delete', value: 'delete', color: 'error', disabled: isDeleting },
          { text: 'Cancel', value: 'cancel' },
        ]}
        onClick={(val) => {
          if (val === 'delete') {
            handleConfirmDelete();
          } else {
            handleConfirmClose();
          }
        }}
      />
    </Box>
  );
}
